import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Mail, Phone, ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen py-8 sm:py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#9A3412] mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl gradient-orange grid place-items-center shadow-md">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Happy Journey 4U</div>
            <div className="text-xl font-bold">HJ4U Campaigns</div>
          </div>
        </div>

        <div className="glass-card p-6 sm:p-8 space-y-5">
          <header>
            <h1 className="text-2xl sm:text-3xl font-bold">Terms of Service</h1>
            <p className="text-sm text-muted-foreground mt-1">Last updated: 11 May 2026</p>
          </header>

          <Section title="1. Acceptance of terms">
            By accessing or using HJ4U Campaigns (the “App”), operated by <strong>Happy Journey 4U</strong>, you agree to these Terms of Service and to our Privacy Policy. If you do not agree, please do not use the App.
          </Section>

          <Section title="2. The App">
            HJ4U Campaigns is an internal tool used by Happy Journey 4U staff to:
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Manage a contact list of customers who have a business relationship with us.</li>
              <li>Create WhatsApp message templates (with optional Gemini AI assistance).</li>
              <li>Submit templates to Meta for approval.</li>
              <li>Send approved campaign messages to customers via the Meta WhatsApp Business Cloud API.</li>
              <li>Receive replies and have two-way conversations with customers, within Meta’s rules.</li>
            </ul>
            The App is provided as a private business tool and is not offered as a public service or as a SaaS product.
          </Section>

          <Section title="3. Eligibility and accounts">
            Only authorised staff of Happy Journey 4U may sign in to the App. You must keep your password confidential and you are responsible for any activity carried out under your account. Notify us immediately at the email below if you suspect unauthorised access.
          </Section>

          <Section title="4. Acceptable use">
            When using the App you must not:
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Send unsolicited or spam WhatsApp messages to people who have not given consent.</li>
              <li>Send content that is illegal, deceptive, abusive, hateful or that violates the rights of others.</li>
              <li>Attempt to bypass Meta’s WhatsApp Business Messaging Policy or any rate limits.</li>
              <li>Reverse engineer, copy, resell or sublicense the App.</li>
              <li>Use the App to harvest, scrape or sell contact information.</li>
            </ul>
            You also agree to comply with all applicable Indian and international laws, including the IT Act, telecom regulations and consumer protection rules.
          </Section>

          <Section title="5. WhatsApp and third-party services">
            The App uses the Meta WhatsApp Business Cloud API, Google Gemini and Supabase. Your use of those services through the App is also subject to their own terms. We are not responsible for any failure, downtime, policy change or data handling by these providers.
          </Section>

          <Section title="6. Message approval and delivery">
            All marketing templates must be approved by Meta before they can be sent. We do not guarantee that any specific template will be approved, that any message will be delivered, or that delivery, read or response data will be 100% accurate. WhatsApp may also restrict, throttle or disable an account that violates its rules.
          </Section>

          <Section title="7. Intellectual property">
            The App, including its source code, design, name and logo, is owned by Happy Journey 4U. You may not copy, modify, distribute or create derivative works without our written permission. Content uploaded by you (images, videos, text) remains your property; by using the App you grant us a limited licence to host, process and transmit that content to deliver the service.
          </Section>

          <Section title="8. Disclaimer of warranties">
            The App is provided <strong>“as is”</strong> and <strong>“as available”</strong>, without warranties of any kind, express or implied, including fitness for a particular purpose, merchantability, uptime, accuracy or non-infringement. Use of the App is at your own risk.
          </Section>

          <Section title="9. Limitation of liability">
            To the maximum extent permitted by law, Happy Journey 4U and its operators will not be liable for any indirect, incidental, special, consequential or punitive damages, including loss of profits, revenue, data, goodwill or business opportunities, arising out of or in connection with your use of the App. Total aggregate liability for any direct claim is limited to INR 1,000.
          </Section>

          <Section title="10. Indemnity">
            You agree to indemnify and hold harmless Happy Journey 4U from any claim, demand or damage (including legal fees) arising from your misuse of the App or your breach of these Terms.
          </Section>

          <Section title="11. Termination">
            We may suspend or terminate your access to the App at any time, without notice, if we believe you have violated these Terms, Meta’s policies or applicable law.
          </Section>

          <Section title="12. Changes">
            We may update these Terms from time to time. The “Last updated” date at the top will reflect the latest version. Continued use of the App after a change constitutes acceptance of the new Terms.
          </Section>

          <Section title="13. Governing law">
            These Terms are governed by the laws of India. Any dispute will be subject to the exclusive jurisdiction of the courts at the registered location of Happy Journey 4U.
          </Section>

          <Section title="14. Contact">
            <div className="space-y-1">
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#9A3412]" /> <a href="mailto:support@happyjourney4u.co.in" className="underline">support@happyjourney4u.co.in</a></div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#9A3412]" /> +91 83197 29163 (WhatsApp Business)</div>
              <div>Happy Journey 4U — India</div>
            </div>
          </Section>
        </div>

        <div className="text-center text-xs text-muted-foreground mt-6">
          <Link to="/privacy" className="underline">Privacy Policy</Link>
          <span className="mx-2">·</span>
          <Link to="/login" className="underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg sm:text-xl font-semibold mb-2">{title}</h2>
      <div className="text-sm leading-relaxed text-[hsl(var(--foreground))]/85 space-y-2">{children}</div>
    </section>
  );
}
